/**
 * De åtte reglene som lager oppfølginger (CRM-2.3). Selve motoren er SQL
 * (`crm_run_follow_up_rules()`); denne filen er katalogen kortet og
 * Drift-panelet leser: etikett, forklaring og standardterskel per regel.
 * Testen holder nøklene og standardverdiene i synk med seed-JSON-en i
 * migrasjonen.
 */

export type FollowUpRuleKey =
  | "offer_no_reply"
  | "offer_deadline_passed"
  | "outreach_no_reply"
  | "meeting_no_next_step"
  | "agreement_expiring"
  | "warm_cooling"
  | "delivered_no_contact"
  | "summary_approval";

export type FollowUpRuleUnit = "days" | "weeks";

export interface FollowUpRuleDef {
  /** Nøkkelen i system_config.crm_follow_up_rules. */
  configKey: string;
  label: string;
  description: string;
  defaultThreshold: number;
  unit: FollowUpRuleUnit;
}

export const FOLLOW_UP_RULES: Record<FollowUpRuleKey, FollowUpRuleDef> = {
  offer_no_reply: {
    configKey: "offer_no_reply_days",
    label: "Tilbud sendt uten svar",
    description: "Tilbudet er sendt, og kunden har ikke svart på prosessen siden.",
    defaultThreshold: 7,
    unit: "days",
  },
  offer_deadline_passed: {
    configKey: "offer_deadline_passed_days",
    label: "Tilbudsfristen er passert",
    description: "Tilbudet er sendt og gyldigheten er utløpt. Lages én gang per prosess.",
    defaultThreshold: 0,
    unit: "days",
  },
  outreach_no_reply: {
    configKey: "outreach_no_reply_days",
    label: "Outreach uten svar",
    description: "Prosessen har stått i Outreach, og kunden har ikke svart.",
    defaultThreshold: 10,
    unit: "days",
  },
  meeting_no_next_step: {
    configKey: "meeting_no_next_step_days",
    label: "Møte uten neste steg",
    description: "Møtet er holdt, men ingenting er logget siden og ingen oppfølging er åpen.",
    defaultThreshold: 1,
    unit: "days",
  },
  agreement_expiring: {
    configKey: "agreement_expiring_weeks",
    label: "Avtale utløper snart",
    description: "En aktiv løpende avtale utløper innen terskelen. Går til kundeeieren.",
    defaultThreshold: 8,
    unit: "weeks",
  },
  warm_cooling: {
    configKey: "warm_cooling_days",
    label: "Varm kunde kjølner",
    description: "Kunden er varm, men faller til medium innen terskelen uten nytt kjøp.",
    defaultThreshold: 30,
    unit: "days",
  },
  delivered_no_contact: {
    configKey: "delivered_no_contact_days",
    label: "Levert uten kontakt",
    description: "Prosjektet er levert, og det har ikke vært kontakt siden. Går til kundeeieren.",
    defaultThreshold: 90,
    unit: "days",
  },
  summary_approval: {
    configKey: "summary_approval_days",
    label: "Referat må godkjennes",
    description: "Lages når et møtereferat er generert (fase 4). Går til møteverten.",
    defaultThreshold: 0,
    unit: "days",
  },
};

export const FOLLOW_UP_RULE_KEYS = Object.keys(FOLLOW_UP_RULES) as FollowUpRuleKey[];

export type FollowUpRuleThresholds = Record<string, number>;

/** Standardterskler som i migrasjonens seed (nøkkel i system_config → verdi). */
export const DEFAULT_RULE_THRESHOLDS: FollowUpRuleThresholds = Object.fromEntries(
  FOLLOW_UP_RULE_KEYS.map((k) => [FOLLOW_UP_RULES[k].configKey, FOLLOW_UP_RULES[k].defaultThreshold]),
);

export function isFollowUpRuleKey(value: string | null | undefined): value is FollowUpRuleKey {
  return !!value && Object.prototype.hasOwnProperty.call(FOLLOW_UP_RULES, value);
}

/** Leser system_config-verdien med standard for manglende eller ugyldige nøkler. */
export function parseRuleThresholds(value: unknown): FollowUpRuleThresholds {
  const stored = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const out: FollowUpRuleThresholds = { ...DEFAULT_RULE_THRESHOLDS };
  for (const key of Object.keys(DEFAULT_RULE_THRESHOLDS)) {
    const v = stored[key];
    if (typeof v === "number" && Number.isFinite(v) && v >= 0) out[key] = v;
  }
  return out;
}

/** «7 dager», «1 dag», «8 uker» og «samme dag» for terskel 0 (dager). */
export function formatRuleThreshold(rule: FollowUpRuleKey, thresholds: FollowUpRuleThresholds = DEFAULT_RULE_THRESHOLDS): string {
  const def = FOLLOW_UP_RULES[rule];
  const n = thresholds[def.configKey] ?? def.defaultThreshold;
  if (def.unit === "weeks") return n === 1 ? "1 uke" : `${n} uker`;
  if (n === 0) return "samme dag";
  return n === 1 ? "1 dag" : `${n} dager`;
}

/** Merket på kortet: «Regel: Tilbud sendt uten svar · 7 dager». */
export function ruleBadge(rule: FollowUpRuleKey, thresholds?: FollowUpRuleThresholds): string {
  return `Regel: ${FOLLOW_UP_RULES[rule].label} · ${formatRuleThreshold(rule, thresholds)}`;
}
